import Valid from '~/assets/images/patient_status_valid_alert.svg'
import { formatApiDate, formatUserDateOfBirth, getUserAge } from '~/shared/lib/date'
import { format } from 'date-fns'
import Exclamation_Alert from '~/assets/images/patient_status_exclamation alert.svg'
import Invalid from '~/assets/images/patient_status_invalid_alert.svg'
import { useEffect, useMemo, useState } from 'react'
import type { FC, ReactNode } from 'react'
import { Box, Chip, IconButton, Popover, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import CakeRoundedIcon from '@mui/icons-material/CakeRounded'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'

import { getUserById } from '~/shared/lib/names'
import type { IPatientInsuranceDetails, PatientUser } from '~/shared/types'
import {
  AccountTypeStatusRing,
  ArrowTooltip,
  BehindProviderFeatureAccessCheck,
  ProspectChip,
  UserAvatar,
} from '~/shared/ui'
import { useFeatureAccess } from '~/shared/hooks/useFeatureAccess'
import { useComplexitySegmentation } from './ComplexitySegmentationService'
import { getPrimaryCareStatusDisplay, useListProgramEnrollments } from '~/entities/program'
import {
  CARE_TEAM_ROLE_GROUPS,
  ROLE_TO_ABBREVIATED,
  CARE_TEAM_CAPTION,
} from '~/shared/constants/careTeamRoles'
import type { Provider } from '~/entities/provider'
import { usePatientDetail } from './usePatientDetail'
import { PatientContactInfo } from './PatientContactInfo'
import PatientDetailPopover from './PatientDetailPopover'
import { PatientStateAndTimeInfo } from './PatientStateAndTimeInfo'
import ElationLink from './ElationLink'
import ComplexityPill from './ComplexityPill/ComplexityPill'
import ComplexityPopover from './ComplexityPill/ComplexityPopover'
import PrimaryCareEnrollmentStatusChip from './PrimaryCareChip/PrimaryCareEnrollmentStatusChip'
import { StickyNoteEditDialog } from './StickyNoteEditDialog'
import { patientsSlice, VerifiedMergeComposer, type MergeSource } from '~/entities/person'
import { useAppDispatch } from '~/app/store/hooks'
import { fireAndForget } from '~/shared/lib/fireAndForget'

// Account status renders as the legacy SVG icon + caption (not a color pill):
// shape carries the meaning, so it stays legible for color-blind users and
// matches the legacy header exactly.
const UserStatusIcon: Record<string, string> = {
  Established: Valid,
  'Established - Insurance Lapsed': Invalid,
  'Do not provide care': Invalid,
  'Not Established': Invalid,
  'Episodic Care': Invalid,
  Unknown: Invalid,
  'FAST ICU': Exclamation_Alert,
  'Escalating Behavior': Exclamation_Alert,
}

// Shared with PatientDetailPopover (the contact-info popover tab descriptor).
export type PatientDetailPopOverTabValueTypes = 'contact_info'
type PatientDetailPopOverTabLabelTypes = 'Contact Info'
export type patientDetailsActiveTabsType = {
  value: PatientDetailPopOverTabValueTypes
  label: PatientDetailPopOverTabLabelTypes
  component: FC<{ patient: PatientUser }>
}

interface PatientDetailTopBarProps {
  patient: PatientUser
  providers: Provider[]
  // Page-level actions that lead the header's action column, in person mode too.
  actions?: ReactNode
}

/**
 * Member Detail header: ringed avatar, name + status chips with the quick
 * actions inline (sticky note, contact info, Elation), meta line, and care-team
 * stack — a single compact block so the tab content keeps its height on laptop
 * screens.
 */
const PatientDetailTopBar: FC<PatientDetailTopBarProps> = props => {
  const {
    patient: {
      first_name: firstName,
      last_name: lastName,
      person: {
        elation_url: elationUrl,
        preferred_name: preferredName,
        dob,
        sex,
        employer_info: employerInfo,
        care_team: careTeam,
        insurance_info: insuranceInfo,
        preferred_language_info: preferredLanguageInfo,
        pronouns,
        provider_note: providerNote,
        provider_note_last_updated_at: providerNoteLastUpdatedAt,
        account_type: accountType,
      },
    },
    providers,
    actions,
  } = props
  const isPerson = !props.patient.id
  const id = props.patient.id || props.patient.person.id

  const { onboardingState } = usePatientDetail()
  const featureAccess = useFeatureAccess()
  const { data: programEnrollments } = useListProgramEnrollments(props.patient.person?.id)
  const { data: complexitySegmentation } = useComplexitySegmentation(props.patient.person?.id)
  const dispatch = useAppDispatch()

  const [complexityAnchorEl, setComplexityAnchorEl] = useState<HTMLElement | null>(null)
  const [contactAnchor, setContactAnchor] = useState<HTMLElement | null>(null)
  const [noteAnchor, setNoteAnchor] = useState<HTMLElement | null>(null)
  const [noteEditing, setNoteEditing] = useState(false)
  const [updatedProviderNote, setUpdatedProviderNote] = useState(providerNote)

  // Same persistence path as the legacy header: the sticky note saves through
  // the patients slice so every other reader of person.provider_note updates.
  useEffect(() => {
    if (updatedProviderNote != props.patient.person.provider_note) {
      const { avatar: _avatar, avatar_large: _avatarLarge, ...person } = props.patient.person
      const newPerson = { ...person, provider_note: updatedProviderNote }
      fireAndForget('[Patient] save provider note', () =>
        dispatch(
          patientsSlice.thunks.updatePatient({
            id: props.patient.id,
            first_name: props.patient.first_name,
            last_name: props.patient.last_name,
            person: newPerson,
          }),
        ),
      )
    }
    // The differs-from-server guard above makes re-runs no-ops, so the full
    // dependency list is safe (unlike the suppressed legacy counterpart).
  }, [
    updatedProviderNote,
    dispatch,
    props.patient.first_name,
    props.patient.id,
    props.patient.last_name,
    props.patient.person,
  ])

  const showSignupBlockedPill = !!onboardingState?.signup_block_reason

  const patientDetailsActiveTabs: patientDetailsActiveTabsType[] = [
    { value: 'contact_info', label: 'Contact Info', component: PatientContactInfo },
  ]

  const styledPreferredName = preferredName ? ` (${preferredName}) ` : ' '
  // A prospect has a preferred name and no legal name, and a lone parenthetical reads as an
  // aside qualifying a name that isn't there.
  const name =
    firstName || lastName
      ? `${firstName ?? ''}${styledPreferredName}${lastName ?? ''}`
      : (preferredName ?? '')
  const dobFormatted = formatUserDateOfBirth(dob ?? null)
  const isPatientBirthday = formatApiDate(dob, 'MM/dd') === format(new Date(), 'MM/dd')
  const employer = employerInfo?.name
  const preferredLanguage =
    preferredLanguageInfo && preferredLanguageInfo.name !== 'English'
      ? preferredLanguageInfo.name
      : null

  const patientCareTeam = useMemo(
    () =>
      (Array.isArray(careTeam) ? careTeam : []).map(providerId =>
        getUserById(providers, providerId),
      ),
    [careTeam, providers],
  )
  // Full care-team names as a role-ordered caption, matching the legacy header
  // ("First Last (NP) · First Last (RN)").
  const careTeamCaption = useMemo(() => {
    const byRole: Record<string, (typeof patientCareTeam)[number]> = {}
    patientCareTeam.forEach(provider => {
      const role = provider?.groups?.find(g => CARE_TEAM_ROLE_GROUPS.has(g.name))?.name ?? 'unknown'
      byRole[role] = provider
    })
    return CARE_TEAM_CAPTION.flatMap(role => {
      const provider = byRole[role]
      if (!provider) return []
      return [`${provider.first_name} ${provider.last_name} (${ROLE_TO_ABBREVIATED[role]})`]
    }).join(' · ')
  }, [patientCareTeam])

  // Only claim "No Care Team Assigned" when the backend truly returned no
  // members (empty array). While ids are present but names are still
  // resolving, render nothing rather than flashing the fallback — this is the
  // at-a-glance row, so a wrong "none" is worse than a brief blank.
  const hasCareTeam = Array.isArray(careTeam) && careTeam.length > 0
  const careTeamText = hasCareTeam ? careTeamCaption || null : 'No Care Team Assigned'

  const getPatientInsuranceDetails = () => {
    const patientInsurance = props.patient.person?.insurance_info
    if (patientInsurance) {
      return {
        coverageStartDate: patientInsurance.coverage_start,
        coverageEndDate: patientInsurance.coverage_end,
        insurancePayerName: patientInsurance.insurance_payer?.name ?? '',
        insurancePayerShortName: patientInsurance.insurance_payer?.short_name ?? null,
      } as IPatientInsuranceDetails
    }
    return null
  }

  const closeNotePopover = () => {
    setNoteAnchor(null)
    setNoteEditing(false)
  }

  const metaSeparator = (
    <Box component="span" sx={{ color: 'grey.200', mx: 0.75 }} aria-hidden>
      ·
    </Box>
  )

  if (props.patient?.person == null) return <div></div>
  if (Object.keys(props.patient.person).length === 0) return <div></div>

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        px: 3,
        py: 1.5,
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.divider}`,
      })}
    >
      <Box sx={{ flex: '0 0 auto' }}>
        <AccountTypeStatusRing accountType={accountType} badgeSize={20}>
          <UserAvatar
            id={id}
            src={props.patient.person.avatar || undefined}
            avatarStyle={{ margin: 0 }}
            fallbackUser={{ id, first_name: firstName, last_name: lastName }}
          />
        </AccountTypeStatusRing>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Name + chips + inline quick actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Typography
            component="h1"
            noWrap
            sx={{ fontSize: '2rem', fontWeight: 600, color: 'text.primary', m: 0 }}
          >
            {name}
          </Typography>
          <Box
            component="span"
            onMouseEnter={
              complexitySegmentation != null
                ? e => setComplexityAnchorEl(e.currentTarget)
                : undefined
            }
            onMouseLeave={
              complexitySegmentation != null ? () => setComplexityAnchorEl(null) : undefined
            }
            sx={{ display: 'inline-flex' }}
          >
            <ComplexityPill predictedSegment={complexitySegmentation?.predicted_segment} />
            {complexitySegmentation != null && (
              <ComplexityPopover
                anchorEl={complexityAnchorEl}
                onClose={() => setComplexityAnchorEl(null)}
                segmentation={complexitySegmentation}
              />
            )}
          </Box>
          {props.patient.is_prospect && <ProspectChip size="small" />}
          {showSignupBlockedPill && (
            <Chip
              data-testid="signup-blocked-pill"
              size="small"
              color="default"
              icon={<BlockRoundedIcon />}
              label="Signup Blocked"
            />
          )}
          <BehindProviderFeatureAccessCheck feature="programs" featureAccess={featureAccess}>
            <PrimaryCareEnrollmentStatusChip
              onboardingStatus={onboardingState?.status ?? ''}
              coverage={props.patient.person.coverage}
              patientInsuranceInfo={getPatientInsuranceDetails()}
              programEnrollments={programEnrollments}
            />
          </BehindProviderFeatureAccessCheck>
        </Box>

        {/* Meta line */}
        <Typography
          data-testid="patient-bio"
          component="div"
          sx={{
            mt: 0.5,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            fontSize: '1.3rem',
            color: 'text.secondary',
          }}
        >
          <Box
            component="span"
            sx={theme => ({ fontFamily: theme.typography.fontFamilyMono, fontSize: '1.2rem' })}
          >
            ID&nbsp;{id}
          </Box>
          {metaSeparator}
          <span>{pronouns ? `${sex} (${pronouns})` : sex}</span>
          {metaSeparator}
          <span>
            {getUserAge(dob)} ({dobFormatted})
          </span>
          {isPatientBirthday && (
            <Box
              component="span"
              sx={{
                color: 'primary.main',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                ml: 1,
              }}
            >
              <CakeRoundedIcon sx={{ fontSize: '1.5rem' }} />
              Today
            </Box>
          )}
          {preferredLanguage && (
            <>
              {metaSeparator}
              <span>{preferredLanguage}</span>
            </>
          )}
          {employer && (
            <>
              {metaSeparator}
              <span>{employer}</span>
            </>
          )}
          {metaSeparator}
          <PatientStateAndTimeInfo patientState={insuranceInfo?.addresses?.[0]?.state} />
        </Typography>

        {/* Status + care team (legacy hierarchy: status stays out of the name
            row so a long label can't push the chips row to wrap) */}
        {(props.patient.user_status || !isPerson) && (
          <Box
            sx={{
              mt: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              flexWrap: 'wrap',
              color: 'text.secondary',
            }}
          >
            {props.patient.user_status && UserStatusIcon[props.patient.user_status] && (
              <>
                <Box
                  component="img"
                  src={UserStatusIcon[props.patient.user_status]}
                  alt="Patient status"
                  sx={{ width: 'auto', height: '1.5rem' }}
                />
                <Typography sx={{ fontSize: '1.25rem' }}>
                  {/* Display label only; the UserStatusIcon lookup keys on the raw value. */}
                  {getPrimaryCareStatusDisplay(props.patient.user_status)}
                </Typography>
              </>
            )}
            {!isPerson && (
              <BehindProviderFeatureAccessCheck feature="care_plans" featureAccess={featureAccess}>
                {careTeamText !== null && (
                  <>
                    <Typography sx={{ fontSize: '1.25rem' }}>&nbsp;·&nbsp;</Typography>
                    <Typography sx={{ fontSize: '1.25rem' }}>{careTeamText}</Typography>
                  </>
                )}
              </BehindProviderFeatureAccessCheck>
            )}
          </Box>
        )}
      </Box>

      {/* offer the ops merge tool to fold this placeholder into a verified destination. */}
      {isPerson && accountType === 'unknown' && (
        <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
          <VerifiedMergeComposer
            source={
              {
                id: props.patient.person.id,
                firstName: firstName ?? null,
                lastName: lastName ?? null,
                dob: dob ?? null,
                memberId: props.patient.person.insurance_info?.member_id ?? null,
                streetAddress:
                  props.patient.person.insurance_info?.addresses?.[0]?.street_address ?? null,
                city: props.patient.person.insurance_info?.addresses?.[0]?.city ?? null,
                zipcode: props.patient.person.insurance_info?.addresses?.[0]?.zipcode ?? null,
                state: props.patient.person.insurance_info?.addresses?.[0]?.state ?? null,
              } satisfies MergeSource
            }
          />
        </Box>
      )}

      {/* Action column: the slot, then the patient-only quick actions behind a rule that
          appears only once the slot rendered something; fixed width so chip wrapping can never
          push it onto its own row */}
      {(actions != null || !isPerson) && (
        <Box
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            // A slot that rendered nothing (the flag off, in person mode) leaves no gap behind.
            '&:empty': { display: 'none' },
          }}
        >
          {actions}
          {!isPerson && (
            <Box
              sx={theme => ({
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                '&:not(:first-child)': {
                  ml: 1,
                  pl: 1,
                  borderLeft: `1px solid ${theme.palette.divider}`,
                },
              })}
            >
              {/* When a note exists the icon itself carries the signal — filled +
              colored, not a small dot — and hovering previews the note text
              (click still opens the editable popover), matching the legacy
              header. */}
              <ArrowTooltip
                title={
                  providerNote ? (
                    <>
                      <Typography sx={{ fontSize: '1.2rem', whiteSpace: 'pre-wrap' }}>
                        {providerNote}
                      </Typography>
                      <Typography sx={{ fontSize: '1rem', opacity: 0.7, mt: 0.5 }}>
                        Last Updated: {formatApiDate(providerNoteLastUpdatedAt, 'MMM dd yyyy')}
                      </Typography>
                    </>
                  ) : (
                    'Sticky note'
                  )
                }
              >
                <IconButton
                  size="small"
                  aria-label={providerNote ? 'Sticky note (has note)' : 'Add sticky note'}
                  onClick={e => setNoteAnchor(e.currentTarget)}
                >
                  {providerNote ? (
                    <StickyNote2Icon sx={{ fontSize: '2rem', color: 'warning.main' }} />
                  ) : (
                    <StickyNote2OutlinedIcon sx={{ fontSize: '2rem' }} />
                  )}
                </IconButton>
              </ArrowTooltip>
              <Popover
                open={Boolean(noteAnchor)}
                anchorEl={noteAnchor}
                onClose={closeNotePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {providerNote && !noteEditing ? (
                  <Box
                    sx={{ p: 1.5, maxWidth: 320 }}
                    role="button"
                    tabIndex={0}
                    onClick={() => setNoteEditing(true)}
                    onKeyDown={() => setNoteEditing(true)}
                  >
                    <Typography>{providerNote}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Last Updated: {formatApiDate(providerNoteLastUpdatedAt, 'MMM dd yyyy')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 1.5 }}>
                    <StickyNoteEditDialog
                      handleStickyNoteSave={note => {
                        setUpdatedProviderNote(note)
                        closeNotePopover()
                      }}
                      previousNote={providerNote ?? undefined}
                    />
                  </Box>
                )}
              </Popover>

              <ArrowTooltip title="Contact Info">
                <IconButton
                  size="small"
                  aria-label="Contact Info"
                  onClick={e => setContactAnchor(e.currentTarget)}
                >
                  <InfoOutlinedIcon sx={{ fontSize: '2rem' }} />
                </IconButton>
              </ArrowTooltip>
              {contactAnchor && (
                <PatientDetailPopover
                  patient={props.patient}
                  anchorEl={contactAnchor}
                  onClose={() => setContactAnchor(null)}
                  activeTabs={patientDetailsActiveTabs}
                />
              )}

              <BehindProviderFeatureAccessCheck
                feature="elation_integration"
                featureAccess={featureAccess}
              >
                <ElationLink elationUrl={elationUrl ?? null} />
              </BehindProviderFeatureAccessCheck>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default PatientDetailTopBar
